import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

const articleContent: Record<string, Record<string, never>> = {
  'what-is-basetao-spreadsheet': {},
};

function ArticleTemplate({ article }: { article: typeof articles[0] }) {
  const relatedArticles = articles.filter((a) => a.slug !== article.slug).slice(0, 4);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6">
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
            Home
          </Link>
          <span className="text-foreground-400 text-sm">/</span>
          <span className="text-sm text-foreground-600">{article.category}</span>
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
          {article.title}
        </h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
          <span>2026-08-10</span>
          <span className="inline-block px-2 py-0.5 bg-primary-50 text-primary-700 rounded-md text-xs font-medium">
            {article.category}
          </span>
        </div>
      </header>

      <div className="relative h-64 md:h-80 rounded-lg overflow-hidden mb-10">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="text-foreground-700 leading-relaxed space-y-4 mb-12">
        <p>
          <strong>{article.title}</strong> is a topic that every Basetao user should understand to maximize their international shopping experience. As more shoppers turn to purchasing agents for sourcing products from Chinese marketplaces, having the right organizational tools becomes essential for managing costs, tracking orders, and ensuring a smooth workflow from order placement to final delivery.
        </p>
        <p>
          In this comprehensive guide, we explore every aspect of {article.title.toLowerCase()} and how it relates to your overall Basetao experience. Whether you are a dropshipping seller managing dozens of orders, a personal shopper building a curated haul, or a reseller optimizing profit margins, the strategies and insights shared here will help you work more efficiently.
        </p>
        <p>
          {article.excerpt}
        </p>

        <h2 className="font-heading text-xl font-bold text-foreground-900 mt-8 mb-2">
          Why This Topic Matters for Basetao Users
        </h2>
        <p>
          Understanding {article.title.toLowerCase()} directly impacts your shopping costs and experience. Without proper organization, many users overspend on shipping, miss warehouse storage deadlines, or lose track of individual orders in complex multi-seller hauls. The information in this guide helps you avoid these common pitfalls.
        </p>

        <h2 className="font-heading text-xl font-bold text-foreground-900 mt-8 mb-2">
          Key Takeaways
        </h2>
        <ul className="space-y-3 list-disc list-inside">
          <li>Learn how to optimize your Basetao workflow for maximum efficiency</li>
          <li>Discover tools and templates that save hours of administrative work</li>
          <li>Understand cost structures to make smarter purchasing decisions</li>
          <li>Master the order tracking process from purchase to delivery</li>
        </ul>

        <h2 className="font-heading text-xl font-bold text-foreground-900 mt-8 mb-2">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4 mt-4">
          <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
            <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
              How does this affect my overall Basetao experience?
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Proper knowledge and organization around {article.title.toLowerCase()} significantly improves your shopping experience by reducing errors, lowering costs, and giving you complete visibility into every order and expense.
            </p>
          </div>
          <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
            <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
              Is this information up to date?
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Yes, this guide is regularly reviewed and updated to reflect the latest Basetao policies, shipping rates, and best practices. Last updated in August 2026.
            </p>
          </div>
          <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
            <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
              Where can I learn more about Basetao Spreadsheet?
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Visit our main guide on <Link to="/article/what-is-basetao-spreadsheet" className="text-primary-600 hover:text-primary-700 font-medium">What Is Basetao Spreadsheet</Link> for comprehensive coverage of the organization system and how it helps manage orders, shipping, and warehouse data.
            </p>
          </div>
        </div>
      </div>

      {/* Related articles */}
      <div className="mt-10 pt-8 border-t border-background-200/70">
        <h3 className="font-heading font-semibold text-foreground-900 mb-4 text-base">
          Related Articles
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {relatedArticles.map((a) => (
            <Link
              key={a.slug}
              to={`/article/${a.slug}`}
              className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-200 transition-all duration-200"
            >
              <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-file-text-line text-sm"></i>
              </div>
              <span className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors">
                {a.title.length > 60 ? a.title.substring(0, 57) + '...' : a.title}
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="mt-10 p-6 bg-primary-50/50 border border-primary-200/50 rounded-lg text-center">
        <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-base">
          Ready to Start Shopping?
        </h3>
        <p className="text-sm text-foreground-600 mb-4">
          Visit the main website to explore products and start organizing with Basetao Spreadsheet.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-6 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors whitespace-nowrap"
          >
            Basetao Spreadsheet
          </a>
          <Link
            to="/"
            className="px-6 py-2.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
          >
            &larr; Back to Home
          </Link>
        </div>
      </div>

      {/* Back to home */}
      <div className="mt-8 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-home-line text-sm"></i>
          </div>
          Return to Basetao Spreadsheet Hub Homepage
        </Link>
      </div>

      {/* Article Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.excerpt,
          "datePublished": "2026-08-10",
          "dateModified": "2026-08-10",
          "author": {
            "@type": "Organization",
            "name": "Basetao Spreadsheet Hub"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Basetao Spreadsheet Hub",
            "url": "https://basetaosheet.run/"
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://basetaosheet.run/article/${article.slug}`
          }
        })}
      </script>

      {/* Breadcrumb Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://basetaosheet.run/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": article.category,
              "item": `https://basetaosheet.run/article/${article.slug}`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": article.title
            }
          ]
        })}
      </script>
    </div>
  );
}

function FullArticle({ slug }: { slug: string }) {
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6">
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
            Home
          </Link>
          <span className="text-foreground-400 text-sm">/</span>
          <span className="text-sm text-foreground-600">Guides</span>
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
          What Is Basetao Spreadsheet? The Complete Guide for Smarter International Shopping
        </h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
          <span>2026-08-10</span>
          <span className="inline-block px-2 py-0.5 bg-primary-50 text-primary-700 rounded-md text-xs font-medium">
            Guides
          </span>
        </div>
      </header>

      <div className="relative h-64 md:h-96 rounded-lg overflow-hidden mb-10">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20wristwatch%20and%20leather%20bound%20notebook%20placed%20on%20rich%20walnut%20wooden%20desk%20surface%20with%20gold%20desk%20lamp%20casting%20warm%20cone%20of%20light%2C%20spreadsheet%20document%20opened%20beside%20them%2C%20sophisticated%20office%20study%20with%20bookshelf%20backdrop%2C%20classic%20timeless%20professional%20atmosphere%2C%20refined%20editorial%20photography%20with%20warm%20amber%20tones%20and%20intellectual%20mood&width=1200&height=600&seq=article-hero-v4&orientation=landscape&nocache=true"
          alt="Basetao Spreadsheet organized workspace"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="text-foreground-700 leading-relaxed">
        <div className="bg-background-100 border border-background-200/70 rounded-lg p-6 mb-10">
          <h2 className="font-heading font-semibold text-foreground-900 mb-3 text-base">Quick Answer</h2>
          <p className="text-foreground-700 text-sm leading-relaxed">
            <strong>Basetao Spreadsheet</strong> is a powerful organization system that helps international shoppers manage their entire purchasing workflow. It combines order tracking, shipping cost calculation, warehouse inventory management, and budget analysis into one structured spreadsheet format. Rather than juggling multiple tools or relying on scattered notes, users can centralize every aspect of their Basetao shopping experience in a single, well-organized document.
          </p>
        </div>

        <h2 className="font-heading text-xl font-bold text-foreground-900 mt-10 mb-4">
          Understanding Basetao Spreadsheet
        </h2>
        <p>
          When you begin shopping through Basetao, you quickly realize that keeping track of multiple orders, various sellers, shipping estimates, and warehouse storage can become overwhelming. A <strong>Basetao Spreadsheet</strong> solves this problem by providing a dedicated framework where every piece of information has its designated place.
        </p>
        <p>
          Think of it as your personal command center for international shopping. Instead of logging into your Basetao account and clicking through dozens of order pages to check statuses, or manually calculating shipping costs with a calculator each time, the spreadsheet brings all critical data together in one view. This approach saves hours of administrative work and dramatically reduces the chance of overlooking important details.
        </p>

        <h2 className="font-heading text-xl font-bold text-foreground-900 mt-10 mb-4">
          Key Components of a Basetao Spreadsheet
        </h2>

        <h3 className="font-heading text-lg font-semibold text-foreground-800 mt-8 mb-3">
          1. Order Tracking Module
        </h3>
        <p>
          The order tracking section is the backbone of any effective <strong>Basetao Spreadsheet</strong>. Here, you record every purchase with essential details: the product name, seller information, purchase date, item price, domestic shipping status, and the order ID assigned by Basetao. As items move through the purchasing pipeline, you update their status from &quot;ordered&quot; to &quot;arrived at warehouse&quot; to &quot;shipped internationally&quot; and finally &quot;delivered.&quot;
        </p>
        <p>
          This systematic tracking eliminates the frustration of wondering where a particular item is in the process. With a quick glance at your spreadsheet, you can see exactly which orders are still pending, which have reached the warehouse, and which are on their way to you. This visibility is especially valuable when you are managing purchases from multiple sellers simultaneously.
        </p>

        <h3 className="font-heading text-lg font-semibold text-foreground-800 mt-8 mb-3">
          2. Shipping Cost Calculator
        </h3>
        <p>
          International shipping costs can vary dramatically based on weight, dimensions, destination country, and the carrier you select. A dedicated <strong>Basetao Spreadsheet</strong> shipping calculator section lets you input estimated weights for each item and compare rates across different shipping methods such as DHL, EMS, and economy lines.
        </p>
        <p>
          By tracking shipping expenses in the spreadsheet, you can make informed decisions about parcel consolidation. For example, you might discover that splitting a large shipment into two medium parcels actually costs less than one oversized package. These insights directly translate into real savings on every order.
        </p>

        <h3 className="font-heading text-lg font-semibold text-foreground-800 mt-8 mb-3">
          3. Warehouse Management
        </h3>
        <p>
          Basetao provides free warehouse storage for a limited period, and after that, storage fees may apply. The warehouse section of your spreadsheet tracks when each item arrived at the warehouse, its storage deadline, and whether you have requested quality control photos. This information helps you plan shipment consolidation before storage fees begin accumulating.
        </p>

        <h3 className="font-heading text-lg font-semibold text-foreground-800 mt-8 mb-3">
          4. Product Research Database
        </h3>
        <p>
          Before making a purchase, savvy shoppers compare products across different sellers. The product research section of your <strong>Basetao Spreadsheet</strong> functions as a comparison database where you save product URLs, seller ratings, price points, and personal notes about each option. Over time, this becomes an invaluable reference that helps you identify which sellers consistently deliver quality products at competitive prices.
        </p>

        <h3 className="font-heading text-lg font-semibold text-foreground-800 mt-8 mb-3">
          5. Budget and Cost Analysis
        </h3>
        <p>
          One of the most powerful features of a properly configured spreadsheet is the ability to see your total spending at a glance. By linking product costs, domestic shipping fees, international shipping expenses, and Basetao service charges in one place, you gain complete financial visibility. This comprehensive view helps you stay within budget, identify areas where costs can be reduced, and make smarter purchasing decisions over time.
        </p>

        <h2 className="font-heading text-xl font-bold text-foreground-900 mt-10 mb-4">
          Pros and Cons of Using Basetao Spreadsheet
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 my-6">
          <div className="bg-primary-50/50 border border-primary-200/50 rounded-lg p-5">
            <h3 className="font-heading font-semibold text-primary-700 text-sm mb-3 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-check-line text-sm"></i>
              </div>
              Pros
            </h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-foreground-700">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 text-primary-500">
                  <i className="ri-checkbox-circle-line text-xs"></i>
                </div>
                <span>Centralized tracking for all orders in one place</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-700">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 text-primary-500">
                  <i className="ri-checkbox-circle-line text-xs"></i>
                </div>
                <span>Automatic cost calculations reduce manual errors</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-700">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 text-primary-500">
                  <i className="ri-checkbox-circle-line text-xs"></i>
                </div>
                <span>Better visibility into shipping costs before committing</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-700">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 text-primary-500">
                  <i className="ri-checkbox-circle-line text-xs"></i>
                </div>
                <span>Historical data helps identify trends and best sellers</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-700">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 text-primary-500">
                  <i className="ri-checkbox-circle-line text-xs"></i>
                </div>
                <span>Completely customizable to fit your specific workflow</span>
              </li>
            </ul>
          </div>
          <div className="bg-accent-50/50 border border-accent-200/50 rounded-lg p-5">
            <h3 className="font-heading font-semibold text-accent-700 text-sm mb-3 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-information-line text-sm"></i>
              </div>
              Cons
            </h3>
            <ul className="space-y-2">
              <li className="flex items-start gap-2 text-sm text-foreground-700">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 text-accent-500">
                  <i className="ri-checkbox-blank-circle-line text-xs"></i>
                </div>
                <span>Requires initial setup time to configure properly</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-700">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 text-accent-500">
                  <i className="ri-checkbox-blank-circle-line text-xs"></i>
                </div>
                <span>Manual data entry needed for each order</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-700">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 text-accent-500">
                  <i className="ri-checkbox-blank-circle-line text-xs"></i>
                </div>
                <span>Spreadsheet skills required for advanced customization</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-foreground-700">
                <div className="w-4 h-4 flex items-center justify-center mt-0.5 text-accent-500">
                  <i className="ri-checkbox-blank-circle-line text-xs"></i>
                </div>
                <span>Not automatically synced with Basetao account data</span>
              </li>
            </ul>
          </div>
        </div>

        <h2 className="font-heading text-xl font-bold text-foreground-900 mt-10 mb-4">
          Real Use Cases: Who Benefits from Basetao Spreadsheet?
        </h2>

        <h3 className="font-heading text-lg font-semibold text-foreground-800 mt-8 mb-3">
          Dropshipping Sellers
        </h3>
        <p>
          Dropshippers managing dozens or even hundreds of orders benefit enormously from a well-organized <strong>Basetao Spreadsheet</strong>. The ability to track which orders have been fulfilled, monitor supplier performance, and calculate profit margins per item transforms a chaotic operation into a manageable business. Sellers can quickly identify which products are performing well and which suppliers consistently deliver on time.
        </p>

        <h3 className="font-heading text-lg font-semibold text-foreground-800 mt-8 mb-3">
          International Resellers
        </h3>
        <p>
          Resellers who purchase products from Chinese marketplaces and sell them in their home countries need precise cost tracking. Every dollar matters when calculating final retail prices. A spreadsheet that accounts for product cost, domestic shipping, international freight, customs duties, and marketplace fees provides the accurate profit analysis that resellers need to price competitively.
        </p>

        <h3 className="font-heading text-lg font-semibold text-foreground-800 mt-8 mb-3">
          Personal Shoppers and Haul Enthusiasts
        </h3>
        <p>
          Even if you are shopping purely for personal use, organizing your purchases with a <strong>Basetao Spreadsheet</strong> makes the experience smoother. When you are building a large haul with items from ten different sellers, the spreadsheet prevents you from forgetting about any single purchase. It also helps you decide the most cost-effective shipping strategy, potentially saving significant money on large consolidated parcels.
        </p>

        <h2 className="font-heading text-xl font-bold text-foreground-900 mt-10 mb-4">
          How to Get Started with Basetao Spreadsheet
        </h2>
        <p>
          Getting started does not require advanced technical skills. You can begin with a simple template that includes columns for order ID, product name, seller, price, order date, warehouse arrival date, shipping method, and delivery status. As you become more comfortable with the system, you can add more sophisticated features like automated cost calculations, conditional formatting for status alerts, and pivot tables for spending analysis.
        </p>
        <p>
          The key is to start simple and expand gradually. Even a basic <strong>Basetao Spreadsheet</strong> with just the essential columns delivers immediate value by keeping your purchasing information organized and accessible. From there, you customize it to match exactly how you prefer to work.
        </p>

        <h2 className="font-heading text-xl font-bold text-foreground-900 mt-10 mb-6">
          Frequently Asked Questions
        </h2>

        <div className="space-y-4">
          <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
            <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
              Do I need special software to use Basetao Spreadsheet?
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              No, a <strong>Basetao Spreadsheet</strong> works with any standard spreadsheet application including Google Sheets, Microsoft Excel, or Apple Numbers. Google Sheets is particularly popular because it is free and accessible from any device with an internet connection.
            </p>
          </div>
          <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
            <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
              Can I share my spreadsheet with others?
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Yes, if you use Google Sheets, you can share your <strong>Basetao Spreadsheet</strong> with team members or business partners with customizable permission levels. However, exercise caution with sensitive order and financial data; only share with trusted individuals and use view-only permissions when appropriate.
            </p>
          </div>
          <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
            <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
              How often should I update my spreadsheet?
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              It is recommended to update your <strong>Basetao Spreadsheet</strong> every time you place a new order and whenever an order status changes. Regular updates, even just a few minutes per day, keep your data accurate and useful. Setting aside five minutes at the end of each shopping session to log new orders is a simple habit that pays off significantly.
            </p>
          </div>
          <div className="bg-background-100 border border-background-200/70 rounded-lg p-5">
            <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">
              Is Basetao Spreadsheet officially provided by Basetao?
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              <strong>Basetao Spreadsheet</strong> is an independent organizational method created by the user community, not an official Basetao product. It uses standard spreadsheet tools to enhance the Basetao shopping experience. The templates and methods described here are community-developed resources designed to complement Basetao&apos;s services.
            </p>
          </div>
        </div>

        {/* Internal Links */}
        <div className="mt-12 pt-8 border-t border-background-200/70">
          <h3 className="font-heading font-semibold text-foreground-900 mb-4 text-base">
            Continue Reading
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/article/basetao-spreadsheet-template"
              className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-200 transition-all duration-200"
            >
              <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-file-text-line text-sm"></i>
              </div>
              <span className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors">
                Free Basetao Spreadsheet Template
              </span>
            </Link>
            <Link
              to="/article/basetao-shipping-cost-guide"
              className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-200 transition-all duration-200"
            >
              <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-ship-line text-sm"></i>
              </div>
              <span className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors">
                Basetao Shipping Cost Guide
              </span>
            </Link>
            <Link
              to="/article/basetao-review-legit"
              className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-200 transition-all duration-200"
            >
              <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-shield-check-line text-sm"></i>
              </div>
              <span className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors">
                Is Basetao Legit? Full Review
              </span>
            </Link>
            <Link
              to="/article/basetao-warehouse-guide"
              className="flex items-center gap-3 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-200 transition-all duration-200"
            >
              <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-store-3-line text-sm"></i>
              </div>
              <span className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors">
                Basetao Warehouse Guide
              </span>
            </Link>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 p-6 bg-primary-50/50 border border-primary-200/50 rounded-lg text-center">
          <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-base">
            Ready to Start Shopping?
          </h3>
          <p className="text-sm text-foreground-600 mb-4">
            Visit the main website to explore products and start organizing with Basetao Spreadsheet.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-md transition-colors whitespace-nowrap"
            >
              Basetao Spreadsheet
            </a>
            <Link
              to="/"
              className="px-6 py-2.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              &larr; Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Back to home */}
      <div className="mt-10 text-center">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-home-line text-sm"></i>
          </div>
          Return to Basetao Spreadsheet Hub Homepage
        </Link>
      </div>

      {/* FAQ Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Do I need special software to use Basetao Spreadsheet?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "No, a Basetao Spreadsheet works with any standard spreadsheet application including Google Sheets, Microsoft Excel, or Apple Numbers. Google Sheets is particularly popular because it is free and accessible from any device with an internet connection."
              }
            },
            {
              "@type": "Question",
              "name": "Can I share my spreadsheet with others?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, if you use Google Sheets, you can share your Basetao Spreadsheet with team members or business partners with customizable permission levels. However, exercise caution with sensitive order and financial data."
              }
            },
            {
              "@type": "Question",
              "name": "How often should I update my spreadsheet?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "It is recommended to update your Basetao Spreadsheet every time you place a new order and whenever an order status changes. Regular updates, even just a few minutes per day, keep your data accurate and useful."
              }
            },
            {
              "@type": "Question",
              "name": "Is Basetao Spreadsheet officially provided by Basetao?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Basetao Spreadsheet is an independent organizational method created by the user community, not an official Basetao product. It uses standard spreadsheet tools to enhance the Basetao shopping experience."
              }
            }
          ]
        })}
      </script>

      {/* Breadcrumb Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://basetaosheet.run/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Guides",
              "item": `https://basetaosheet.run/article/${slug}`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "What Is Basetao Spreadsheet?"
            }
          ]
        })}
      </script>
      {/* Article Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "What Is Basetao Spreadsheet? The Complete Guide for Smarter International Shopping",
          "description": "Discover how Basetao Spreadsheet transforms your international shopping workflow with powerful organization tools.",
          "datePublished": "2026-08-10",
          "dateModified": "2026-08-10",
          "author": {
            "@type": "Organization",
            "name": "Basetao Spreadsheet Hub"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Basetao Spreadsheet Hub",
            "url": "https://basetaosheet.run/"
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://basetaosheet.run/article/${slug}`
          }
        })}
      </script>
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles.find((a) => a.slug === slug) : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <title>Article Not Found - Basetao Spreadsheet Hub</title>
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-32 pb-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist.</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
              &larr; Back to Home
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const isFullArticle = slug && articleContent[slug];

  return (
    <>
      <title>{article.title}</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={`Basetao Spreadsheet, ${article.category}, ${article.title.split(':')[0]}`} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <article className="pt-28 pb-16">
            {isFullArticle ? (
              <FullArticle slug={slug} />
            ) : (
              <ArticleTemplate article={article} />
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}